/**
 * WifiCsiSetup.tsx — v0.4.9.4.6 (placeholder) — target v0.6
 *
 * Wi-Fi CSI (Channel State Information) sensing — the next breakthrough
 * beyond face recognition. Uses subcarrier amplitude/phase changes to
 * infer human presence, gait, posture, breathing — even person identity
 * (with per-person enrollment samples).
 *
 * This is a placeholder until v0.6 ships. The placeholder shows:
 *   - what CSI is and why it works
 *   - the planned data flow
 *   - the open-source starting points we're evaluating
 *
 * Why this matters:
 *   - Increases recognition to ~100% in access-control scenarios by
 *     adding a second corroborating signal
 *   - Removes the false-positives that visual recognition alone can't
 *     eliminate (similar-looking faces, photos of faces, masks)
 *   - Works in low light / through walls (with sufficient RSSI margin)
 *
 * Hardware target: ESP32 with esp-csi firmware (Apache-2.0). See
 * ROADMAP.md for the open-source library survey.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Construction, Wifi, Activity, Users, ShieldCheck, BookOpen } from "lucide-react";

export default function WifiCsiSetup() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {/* Headline banner */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/30 p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Construction className="w-5 h-5 text-amber-700" />
          <h2 className="text-lg font-semibold text-amber-900">
            {t('aiTraining.comingSoonTitle')}
          </h2>
          <span className="px-2 py-0.5 rounded text-xs bg-amber-200 text-amber-900">
            {t('aiTraining.targetVersion', { version: '0.6' })}
          </span>
        </div>
        <p className="text-sm text-gray-900/90">
          {t('aiTraining.wifiCsi.description')}
        </p>
      </div>

      {/* The four capabilities */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Cap
          Icon={Activity}
          title={t('aiTraining.wifiCsi.caps.presence')}
          body={t('aiTraining.wifiCsi.caps.presenceBody')}
        />
        <Cap
          Icon={Users}
          title={t('aiTraining.wifiCsi.caps.activity')}
          body={t('aiTraining.wifiCsi.caps.activityBody')}
        />
        <Cap
          Icon={ShieldCheck}
          title={t('aiTraining.wifiCsi.caps.identification')}
          body={t('aiTraining.wifiCsi.caps.identificationBody')}
        />
        <Cap
          Icon={Wifi}
          title={t('aiTraining.wifiCsi.caps.authentication')}
          body={t('aiTraining.wifiCsi.caps.authenticationBody')}
        />
      </div>

      {/* Pipeline diagram */}
      <div className="rounded-lg border border-gray-300 bg-white p-5">
        <h3 className="text-sm font-semibold mb-3">{t('aiTraining.wifiCsi.pipelineTitle')}</h3>
        <pre className="text-xs font-mono whitespace-pre overflow-auto p-3 bg-gray-50 rounded">
{`ESP32 CSI transmitter/receiver
        ↓
CSI frames over serial, UDP, or Wi-Fi
        ↓
Python preprocessing
        ↓
features: amplitude, phase, Doppler, temporal patterns
        ↓
classifier: CNN, LSTM, Transformer, SVM, or k-NN
        ↓
person ID / unknown / no person`}
        </pre>
      </div>

      {/* Open-source starting points */}
      <div className="rounded-lg border border-gray-300 bg-white p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          {t('aiTraining.wifiCsi.refsTitle')}
        </h3>
        <ul className="space-y-2 text-xs">
          <Ref
            title="Espressif esp-csi"
            body={t('aiTraining.wifiCsi.refs.espCsi')}
            url="https://github.com/espressif/esp-csi"
          />
          <Ref
            title="WiFi-CSI-Human-Pose-Detection"
            body={t('aiTraining.wifiCsi.refs.wifiVision')}
            url="https://github.com/exception-s/WiFi-CSI-Human-Pose-Detection"
          />
          <Ref
            title="Awesome WiFi CSI Sensing"
            body={t('aiTraining.wifiCsi.refs.awesome')}
            url="https://github.com/linteresa/Awesome-WiFi-CSI-Sensing"
          />
          <Ref
            title="WiFi-Vision"
            body={t('aiTraining.wifiCsi.refs.wifiVisionProject')}
            url="https://github.com/xyanchen/WiFi-Vision"
          />
        </ul>
      </div>

      <p className="text-xs text-gray-600">
        {t('aiTraining.comingSoonFootnote')}
      </p>
    </div>
  );
}

function Cap({ Icon, title, body }: { Icon: React.ComponentType<any>; title: string; body: string }) {
  return (
    <div className="rounded border border-gray-300 bg-white p-4 space-y-2">
      <div className="flex items-center gap-2 text-gray-900">
        <Icon className="w-4 h-4" />
        <div className="font-medium text-sm">{title}</div>
      </div>
      <p className="text-xs text-gray-600">{body}</p>
    </div>
  );
}

function Ref({ title, body, url }: { title: string; body: string; url: string }) {
  return (
    <li className="flex gap-2">
      <span className="text-gray-900">•</span>
      <div>
        <a href={url} target="_blank" rel="noreferrer" className="font-medium text-blue-700 hover:underline">
          {title}
        </a>
        <span className="text-gray-600"> — {body}</span>
      </div>
    </li>
  );
}
